import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  ArrowRight,
  Check,
  ChefHat,
  CreditCard,
  House,
  Package,
  Receipt,
  RefreshCw,
  MessageCircle,
  MapPin,
  Copy,
  Share2,
  ShoppingBag,
  Sparkles,
  Store,
  TableProperties,
  Timer,
  User,
  UtensilsCrossed,
  XCircle,
} from "lucide-react"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { EOrderItemChargeType } from "../../Enum/EOrderItemChargeType"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { companyMemory, orderMemory } from "../../Objects/Memory/Memory"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { OrderDrink } from "../../Objects/Order/OrderDrink"
import { OrderMeal } from "../../Objects/Order/OrderMeal"
import { LoginRedirectView } from "../Components/LoginRedirectView"
import { EAppView } from "../../Enum/EAppViewType"
import { useLoad } from "../../context/useLoadContext"
import { LoadingComponet } from "../Components/LoadingComponet"
import { PaymentDetails } from "../Components/PaymentDetails"
import { useReloadMemory } from "../../hooks/useReloadMemory"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { usePayment } from "../../hooks/Payment/usePayment"
import { setDelay } from "../../Objects/Generics/Helps"
import { useRedirectPayment } from "../../context/useRedirectPayment"
import { getWhatsappSupportLink } from "../Helpers/Validators"
import { normalizeReservationDateForInput } from "../../Extensions/ReservationExtensions"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { useTableSelfServiceOrders } from "../../hooks/Order/useTableSelfServiceOrders"
import { useConfirmation } from "../../context/useConfirmationContext"

interface ComandaViewProps {
  mode: EOrderPlaceType
  setCurrentView: (view: EAppView) => void
  tableSelfService?: { tableId: number; tableNumber: number; canCreateOrders: boolean }
}

const renderProviderChargeReference = (externalReference: string | undefined, label: string) => {
  if (!externalReference) {
    return null
  }

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 shadow-sm">
      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-sky-700">Mercado Pago</p>

      <p className="mt-2 font-semibold">{label}</p>

      <p className="mt-1 break-all font-mono text-xs sm:text-sm">{externalReference}</p>
    </div>
  )
}

const ComandaView: React.FC<ComandaViewProps> = ({ mode, setCurrentView, tableSelfService }) => {
  const { restaurantSlug } = useParams<{ restaurantSlug: string }>()

  const { t } = useTranslation()

  const { auth } = usePublicViewContext()

  const { handleReloadPublicOrder } = useReloadMemory()

  const { handleCancelTableSelfServiceOrder, handleReloadTableSelfServiceOrders } = useTableSelfServiceOrders()

  const { setNewConfirmation } = useConfirmation()

  const { isFlagLoading } = useLoad()

  const { startRedirect, stopRedirect } = useRedirectPayment()

  const [lastUpdateDate, setLastUpdateDate] = useState<String>(Date().toReadableDate())

  const [redirectingOrderId, setRedirectingOrderId] = useState<number | null>(null)

  const [copiedPickupOrderId, setCopiedPickupOrderId] = useState<number | null>(null)

  const [cancelingOrderId, setCancelingOrderId] = useState<number | null>(null)

  const [, setNow] = useState<number>(Date.now())

  const { createOrderOnlineCheckout, getRestaurantPaymentProviders } = usePayment()

  const [hasCustomerCheckoutEnabled, setHasCustomerCheckoutEnabled] = useState(false)

  useEffect(() => {
    void (async () => {
      const providers = await getRestaurantPaymentProviders()

      setHasCustomerCheckoutEnabled(providers.some((p) => p.isOnlinePaymentEnabled || p.isInPersonPaymentEnabled))
    })()
  }, [])

  const publicMenuTheme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(publicMenuTheme)

  const whatsappPhone = companyMemory.companySocialMediaEntity?.whatsAppPhone?.replace(/\D/g, "") ?? ""

  const hasValidWhatsappPhone = whatsappPhone.length >= 12 && whatsappPhone.length <= 13

  const pickupAddressLine = companyMemory.addressEntity
    ? `${companyMemory.addressEntity.street}, ${companyMemory.addressEntity.number} - ${companyMemory.addressEntity.neighborhood}, ${companyMemory.addressEntity.city}/${companyMemory.addressEntity.state}`
    : ""

  const pickupMapsLink =
    companyMemory.addressEntity?.latitude && companyMemory.addressEntity?.longitude
      ? `https://www.google.com/maps?q=${companyMemory.addressEntity.latitude},${companyMemory.addressEntity.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pickupAddressLine)}`

  const pickupWhatsappLink = `https://wa.me/?text=${encodeURIComponent(
    t("publicMenu:tracking.pickup_address_share_message", {
      restaurant: companyMemory.companyName || "Restaurante",
      address: pickupAddressLine,
    }),
  )}`

  const handleCopyPickupAddress = async (orderId: number) => {
    if (!pickupAddressLine) {
      return
    }

    try {
      await navigator.clipboard.writeText(pickupAddressLine)
      setCopiedPickupOrderId(orderId)
      window.setTimeout(() => setCopiedPickupOrderId(null), 2000)
    } catch {
      setCopiedPickupOrderId(null)
    }
  }

  useEffect(() => {
    if (!auth.customer && !tableSelfService) {
      return
    }

    const load = async () => {
      if (tableSelfService) {
        await handleReloadTableSelfServiceOrders(restaurantSlug)
      } else {
        await handleReloadPublicOrder(restaurantSlug)
      }
      setLastUpdateDate(Date().toReadableDate())
    }

    load()

    const interval = setInterval(load, 30 * 1000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)

    return () => clearInterval(interval)
  }, [])

  const filteredByMode = orderMemory.listData.filter((o) => {
    if (mode === EOrderPlaceType.Local) {
      return o.eOrderPlaceType === EOrderPlaceType.Local
    }

    return o.eOrderPlaceType === EOrderPlaceType.Delivery
  })

  const activeOrders = filteredByMode.filter((order) => !order.isFinished())

  if (!auth.customer && !tableSelfService) {
    return (
      <LoginRedirectView
        title={t("publicMenu:history.title")}
        description={t("publicMenu:history.description")}
        setCurrentView={setCurrentView}
      />
    )
  }

  if (isFlagLoading) {
    return <LoadingComponet />
  }

  if (activeOrders.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate}>
        <div className="mx-auto max-w-xl space-y-8 px-4 py-16 text-center animate-in fade-in duration-700 sm:py-24">
          <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 via-white to-amber-50 text-orange-500 shadow-sm sm:h-32 sm:w-32">
            {mode === EOrderPlaceType.Local ? <UtensilsCrossed size={48} /> : <Package size={48} />}
          </div>

          <div className="space-y-3">
            <h2
              className="text-2xl font-black tracking-tight text-gray-900"
              style={isLegacyTheme ? undefined : { color: publicMenuTheme.emptyStateTitleColor }}
            >
              {mode === EOrderPlaceType.Local
                ? t("publicMenu:tracking.empty_table")
                : t("publicMenu:tracking.empty_active")}
            </h2>

            <p
              className="mx-auto max-w-sm font-medium text-gray-500"
              style={isLegacyTheme ? undefined : { color: publicMenuTheme.emptyStateDescriptionColor }}
            >
              {mode === EOrderPlaceType.Local
                ? t("publicMenu:tracking.empty_logged_in")
                : t("publicMenu:tracking.empty_guest")}
            </p>
          </div>
        </div>
      </Animate>
    )
  }

  const baseSteps = [
    {
      status: EOrderStatusType.Pending,
      label: t("publicMenu:tracking.status_confirmed"),
      icon: Receipt,
      color: "bg-blue-500",
      textColor: "text-blue-500",
      ring: "ring-blue-100",
    },
    {
      status: EOrderStatusType.Preparing,
      label: t("publicMenu:tracking.status_cooking"),
      icon: ChefHat,
      color: "bg-orange-500",
      textColor: "text-orange-500",
      ring: "ring-orange-100",
    },
    {
      status: EOrderStatusType.PreparingFinished,
      label: t("publicMenu:tracking.status_ready"),
      icon: Sparkles,
      color: "bg-green-500",
      textColor: "text-green-500",
      ring: "ring-green-100",
    },
  ]

  const deliverySteps = [
    {
      status: EOrderStatusType.InDelivery,
      label: t("publicMenu:tracking.status_on_the_way"),
      icon: Package,
      color: "bg-purple-500",
      textColor: "text-purple-500",
      ring: "ring-purple-100",
    },
  ]

  const storePickupSteps = [
    {
      status: EOrderStatusType.InDelivery,
      label: t("publicMenu:tracking.status_waiting_pickup"),
      icon: Store,
      color: "bg-emerald-500",
      textColor: "text-emerald-500",
      ring: "ring-emerald-100",
    },
  ]

  const finalStep = {
    status: EOrderStatusType.Delivered,
    label: t("publicMenu:tracking.status_delivered"),
    icon: House,
    color: "bg-gray-500",
    textColor: "text-gray-500",
    ring: "ring-gray-200",
  }

  const getSteps = (order: OrderEntity) => {
    const waitingPaymentStep = {
      status: EOrderStatusType.WaitingOnlinePayment,
      label: order.orderPaymentEntity.isOnlinePayment
        ? t("publicMenu:tracking.status_waiting_online_payment")
        : t("publicMenu:tracking.status_pay_on_delivery"),
      icon: CreditCard,
      color: "bg-amber-500",
      textColor: "text-amber-500",
      ring: "ring-amber-100",
    }

    if (mode !== EOrderPlaceType.Delivery) {
      return [waitingPaymentStep, ...baseSteps, finalStep]
    }

    return order.isStorePickup
      ? [waitingPaymentStep, ...baseSteps, ...storePickupSteps, finalStep]
      : [waitingPaymentStep, ...baseSteps, ...deliverySteps, finalStep]
  }

  const getPaymentCountdownLabel = (order: OrderEntity) => {
    const expirationDate = new Date(order.createdAtUtc || order.date)

    expirationDate.setMinutes(expirationDate.getMinutes() + companyMemory.orderPaymentTimeoutMinutes)

    const remainingMs = expirationDate.getTime() - Date.now()

    if (remainingMs <= 0) {
      return t("publicMenu:tracking.payment_expired")
    }

    const totalSeconds = Math.floor(remainingMs / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    return t("publicMenu:tracking.payment_remaining", {
      minutes: String(minutes).padStart(2, "0"),
      seconds: String(seconds).padStart(2, "0"),
    })
  }

  const getPaymentRetentionLabel = () =>
    t("publicMenu:tracking.payment_retention", {
      days: companyMemory.unpaidOrderRetentionDays,
    })

  const isPaymentExpired = (order: OrderEntity) => {
    const expirationDate = new Date(order.createdAtUtc || order.date)
    expirationDate.setMinutes(expirationDate.getMinutes() + companyMemory.orderPaymentTimeoutMinutes)

    return expirationDate.getTime() <= Date.now()
  }

  const handlePayOrderAgain = async (order: OrderEntity) => {
    setRedirectingOrderId(order.id)
    startRedirect()

    try {
      const response = await createOrderOnlineCheckout(order.id)

      await setDelay(2000)

      if (!response?.initPoint) {
        return
      }

      window.location.href = response.initPoint
    } finally {
      setRedirectingOrderId(null)
      stopRedirect()
    }
  }

  const handleCancelOrder = (order: OrderEntity) => {
    setNewConfirmation(
      t("tableSelfService:cancel_order_title"),
      t("tableSelfService:cancel_order_confirmation", { id: order.id }),
      async () => {
        setCancelingOrderId(order.id)

        try {
          const canceled = await handleCancelTableSelfServiceOrder(restaurantSlug, order)

          if (canceled) {
            setLastUpdateDate(Date().toReadableDate())
          }
        } finally {
          setCancelingOrderId(null)
        }
      },
    )
  }

  const canCustomerPayFromComanda = (order: OrderEntity) => {
    if (tableSelfService) {
      return false
    }

    if (!hasCustomerCheckoutEnabled) {
      return false
    }

    if (order.eOrderPlaceType === EOrderPlaceType.Local) {
      return order.eOrderStatusType !== EOrderStatusType.Delivered
    }

    return order.eOrderStatusType === EOrderStatusType.WaitingOnlinePayment && order.orderPaymentEntity.isOnlinePayment
  }

  const getProgressWidth = (currentStatusIndex: number, filteredStepsLength: number) => {
    if (filteredStepsLength <= 1 || currentStatusIndex <= 0) {
      return "0%"
    }

    return `${(currentStatusIndex / (filteredStepsLength - 1)) * 100}%`
  }

  const renderOrder = (order: OrderEntity) => {
    const filteredSteps = getSteps(order)

    const currentStatusIndex = filteredSteps.findIndex((s) => s.status === order.eOrderStatusType)

    const currentStep = filteredSteps[currentStatusIndex] ?? filteredSteps[0]

    const isCompleted = order.eOrderStatusType === EOrderStatusType.Delivered

    const allItems = [...order.orderMeals, ...order.orderDrinks]

    const totalItems = allItems.length

    const totalAdditionalValue = allItems.reduce((sum, item) => sum + item.getValueAddtions(), 0)

    const totalDiscountValue = allItems.reduce((sum, item) => sum + item.valueDiscount, 0)

    return (
      <Animate key={order.id} type={AnimationTypes.fadeInTraslate}>
        <div
          className="mb-8 overflow-hidden rounded-lg border-2 border-orange-200 bg-white shadow-[0_30px_80px_-45px_rgba(15,23,42,0.35)] ring-1 ring-black/5 animate-in slide-in-from-bottom-6 duration-500"
          style={
            isLegacyTheme
              ? undefined
              : {
                  backgroundColor: publicMenuTheme.surfaceColor,
                  borderColor: publicMenuTheme.borderColor,
                  borderWidth: publicMenuTheme.borderThickness,
                }
          }
        >
          <div
            className="border-b border-orange-200 bg-gradient-to-r from-white via-orange-50 to-orange-100 p-4 text-slate-800 md:p-6"
            style={
              isLegacyTheme
                ? undefined
                : {
                    borderColor: publicMenuTheme.borderColor,
                    color: publicMenuTheme.textColor,
                    background: publicMenuTheme.useGradientHeader
                      ? `linear-gradient(135deg, ${publicMenuTheme.headerBackgroundColor} 0%, ${publicMenuTheme.surfaceColor} 55%, ${publicMenuTheme.backgroundColor} 100%)`
                      : publicMenuTheme.headerBackgroundColor,
                  }
            }
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-4">
                <div
                  className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-orange-600"
                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                >
                  <span
                    className="rounded-lg border border-orange-200 bg-white px-3 py-1.5 shadow-sm"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                    }
                  >
                    {t("publicMenu:tracking.order_number", { id: order.id })}
                  </span>

                  <span
                    className="rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-slate-500 shadow-sm"
                    style={
                      isLegacyTheme
                        ? undefined
                        : {
                            backgroundColor: publicMenuTheme.surfaceColor,
                            borderColor: publicMenuTheme.borderColor,
                            color: publicMenuTheme.textColor,
                          }
                    }
                  >
                    {new Date(order.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500 shadow-sm"
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              backgroundColor: publicMenuTheme.surfaceColor,
                              borderColor: publicMenuTheme.borderColor,
                              color: publicMenuTheme.accentColor,
                            }
                      }
                    >
                      {order.eOrderPlaceType === EOrderPlaceType.Delivery ? (
                        <Package size={22} />
                      ) : (
                        <TableProperties size={22} />
                      )}
                    </div>

                    <div>
                      <h3
                        className="text-2xl font-black uppercase tracking-tight sm:text-3xl"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                      >
                        {order.eOrderPlaceType === EOrderPlaceType.Delivery
                          ? t("publicMenu:tracking.delivery")
                          : `${t("publicMenu:tracking.table")} ${order.tableId}`}
                      </h3>

                      {mode === EOrderPlaceType.Local && order.getCustomerDisplayName() && (
                        <p className="mt-1 flex items-center gap-1.5 text-sm font-black text-orange-700">
                          <User size={14} />
                          {order.getCustomerDisplayName()}
                        </p>
                      )}

                      <p className="text-sm font-medium text-slate-500">
                        {mode === EOrderPlaceType.Local
                          ? t("publicMenu:tracking.follow_local_order")
                          : t("publicMenu:tracking.follow_delivery_order")}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div
                      className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm"
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              backgroundColor: publicMenuTheme.surfaceColor,
                              borderColor: publicMenuTheme.borderColor,
                              borderWidth: publicMenuTheme.borderThickness,
                            }
                      }
                    >
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                      >
                        {t("publicMenu:tracking.items")}
                      </p>

                      <p
                        className="mt-1 text-lg font-black text-slate-800"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                      >
                        {totalItems}
                      </p>
                    </div>

                    <div
                      className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm"
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              backgroundColor: publicMenuTheme.surfaceColor,
                              borderColor: publicMenuTheme.borderColor,
                              borderWidth: publicMenuTheme.borderThickness,
                            }
                      }
                    >
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                      >
                        {t("publicMenu:tracking.additionals")}
                      </p>

                      <p
                        className="mt-1 text-lg font-black text-slate-800"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                      >
                        R$ {totalAdditionalValue.toFixed(2)}
                      </p>
                    </div>

                    <div
                      className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm"
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              backgroundColor: publicMenuTheme.surfaceColor,
                              borderColor: publicMenuTheme.borderColor,
                              borderWidth: publicMenuTheme.borderThickness,
                            }
                      }
                    >
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                      >
                        {t("publicMenu:tracking.discounts")}
                      </p>

                      <p
                        className="mt-1 text-lg font-black text-slate-800"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                      >
                        R$ {totalDiscountValue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {!isCompleted && (
                <div className="space-y-3 lg:max-w-xs lg:min-w-[250px]">
                  <div
                    className="flex items-center gap-3 rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm"
                    style={
                      isLegacyTheme
                        ? undefined
                        : {
                            backgroundColor: publicMenuTheme.surfaceColor,
                            borderColor: publicMenuTheme.borderColor,
                            borderWidth: publicMenuTheme.borderThickness,
                          }
                    }
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-sm shadow-sm ${currentStep.textColor}`}
                      style={
                        isLegacyTheme
                          ? undefined
                          : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                      }
                    >
                      <currentStep.icon size={18} />
                    </div>

                    <div>
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                      >
                        {t("publicMenu:tracking.current_status")}
                      </p>

                      <p
                        className="text-sm font-black uppercase tracking-wide text-slate-800"
                        style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                      >
                        {currentStep.label}
                      </p>
                    </div>
                  </div>

                  {tableSelfService &&
                    order.createdByTableSelfService &&
                    (order.canBeCanceledByTableSelfService && tableSelfService.canCreateOrders ? (
                      <button
                        type="button"
                        onClick={() => handleCancelOrder(order)}
                        disabled={cancelingOrderId === order.id}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-400 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-back shadow-sm transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.button.handleCancelOrderOrder"
                      >
                        <XCircle size={17} className={cancelingOrderId === order.id ? "animate-spin" : ""} />

                        {cancelingOrderId === order.id
                          ? t("tableSelfService:canceling_order")
                          : t("tableSelfService:cancel_order")}
                      </button>
                    ) : (
                      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-xs font-bold text-amber-800">
                        {tableSelfService.canCreateOrders
                          ? t("tableSelfService:cancel_order_started")
                          : t("tableSelfService:cancel_order_call_waiter")}
                      </p>
                    ))}

                  {order.eOrderStatusType === EOrderStatusType.WaitingOnlinePayment &&
                    order.orderPaymentEntity.isOnlinePayment && (
                      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 text-slate-900 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                            <Timer size={18} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-700">
                              {t("publicMenu:tracking.pending_payment")}
                            </p>

                            <p className="mt-1 text-sm font-bold text-amber-900">{getPaymentCountdownLabel(order)}</p>

                            {isPaymentExpired(order) && (
                              <p className="mt-2 text-xs font-semibold text-amber-800">{getPaymentRetentionLabel()}</p>
                            )}
                          </div>
                        </div>

                        {!isPaymentExpired(order) && (
                          <button
                            onClick={() => handlePayOrderAgain(order)}
                            disabled={redirectingOrderId === order.id}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.button.handlePayOrderAgainOrder"
                          >
                            {redirectingOrderId === order.id ? (
                              <RefreshCw size={16} className="animate-spin" />
                            ) : (
                              <ArrowRight size={16} />
                            )}
                            {redirectingOrderId === order.id
                              ? t("publicMenu:tracking.redirecting_payment")
                              : t("publicMenu:tracking.pay_now")}
                          </button>
                        )}
                      </div>
                    )}

                  {canCustomerPayFromComanda(order) &&
                    order.eOrderStatusType !== EOrderStatusType.WaitingOnlinePayment && (
                      <button
                        onClick={() => handlePayOrderAgain(order)}
                        disabled={redirectingOrderId === order.id}
                        className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.button.handlePayOrderAgainOrder2"
                      >
                        {redirectingOrderId === order.id ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : (
                          <ArrowRight size={16} />
                        )}

                        {redirectingOrderId === order.id
                          ? t("publicMenu:tracking.redirecting_payment")
                          : t("publicMenu:tracking.pay_now")}
                      </button>
                    )}

                  {hasValidWhatsappPhone && (
                    <a
                      href={getWhatsappSupportLink(order.id, whatsappPhone)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 rounded-xl border-2 border-emerald-400 bg-white px-4 py-3 text-sm font-black text-black shadow-sm transition hover:bg-emerald-600"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.link.askWhatsapp"
                    >
                      <MessageCircle size={16} />
                      {t("publicMenu:tracking.ask_on_whatsapp")}
                    </a>
                  )}

                  {order.eOrderPlaceType === EOrderPlaceType.Delivery && order.isStorePickup && (
                    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700">
                        {t("publicMenu:tracking.pickup_address_title")}
                      </p>

                      <p className="mt-2 text-xs font-semibold text-emerald-900">
                        {pickupAddressLine || t("publicMenu:tracking.pickup_address_missing")}
                      </p>

                      <div className="mt-3 grid grid-cols-1 gap-2">
                        <a
                          href={pickupMapsLink}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center justify-center gap-2 rounded-lg border border-emerald-400 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-emerald-700 ${!pickupAddressLine ? "pointer-events-none opacity-50" : ""}`}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.link.pickupViewAddress"
                        >
                          <MapPin size={14} />
                          {t("publicMenu:tracking.pickup_view_address")}
                        </a>

                        <button
                          type="button"
                          onClick={() => handleCopyPickupAddress(order.id)}
                          disabled={!pickupAddressLine}
                          className={`appearance-none flex items-center justify-center gap-2 rounded-lg border border-emerald-400 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-emerald-700 ${!pickupAddressLine ? "pointer-events-none opacity-50" : ""}`}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.button.handleCopyPickupAddressOrderId"
                        >
                          <Copy size={14} />
                          {copiedPickupOrderId === order.id
                            ? t("publicMenu:tracking.pickup_address_copied")
                            : t("publicMenu:tracking.pickup_copy_address")}
                        </button>

                        <a
                          href={pickupWhatsappLink}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center justify-center gap-2 rounded-lg border border-emerald-400 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-emerald-700 ${!pickupAddressLine ? "pointer-events-none opacity-50" : ""}`}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pages.comandaView.link.pickupShareWhatsapp"
                        >
                          <Share2 size={14} />
                          {t("publicMenu:tracking.pickup_share_whatsapp")}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isCompleted && (
            <div
              className="border-b border-orange-100 bg-white px-4 py-5 md:px-6 md:py-6"
              style={
                isLegacyTheme
                  ? undefined
                  : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
              }
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p
                    className="text-[10px] font-black uppercase tracking-[0.24em] text-orange-500"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                  >
                    {t("publicMenu:tracking.progress")}
                  </p>

                  <h4
                    className="text-sm font-black text-slate-900 sm:text-base"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                  >
                    {t("publicMenu:tracking.progress_description")}
                  </h4>
                </div>

                <span
                  className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-orange-600"
                  style={
                    isLegacyTheme
                      ? undefined
                      : {
                          backgroundColor: publicMenuTheme.categoryBackgroundColor,
                          borderColor: publicMenuTheme.borderColor,
                          color: publicMenuTheme.accentColor,
                        }
                  }
                >
                  {currentStatusIndex + 1}/{filteredSteps.length}
                </span>
              </div>

              <div className="relative hidden items-start justify-between gap-3 md:flex">
                <div className="absolute left-[7%] right-[7%] top-6 h-1 rounded-full bg-slate-200" />

                <div
                  className={`absolute left-[7%] top-6 h-1 rounded-full transition-all duration-700 ${currentStep.color}`}
                  style={{ width: `calc(${getProgressWidth(currentStatusIndex, filteredSteps.length)} * 0.86)` }}
                />

                {filteredSteps.map((step, idx) => {
                  const stepDone = idx <= currentStatusIndex
                  const stepActive = idx === currentStatusIndex

                  return (
                    <div key={step.label} className="relative z-10 flex flex-1 flex-col items-center text-center">
                      <div
                        className={[
                          "flex h-11 w-11 items-center justify-center rounded-lg border-2 border-white text-sm shadow-sm transition-all duration-300",
                          stepDone ? `${step.color} text-white` : "bg-white text-slate-300",
                          stepActive ? `scale-110 ring-4 ${step.ring}` : "",
                        ].join(" ")}
                      >
                        {stepDone && !stepActive ? <Check size={18} /> : <step.icon size={18} />}
                      </div>
                      <span
                        className={`mt-3 max-w-[110px] text-[10px] font-bold uppercase tracking-[0.12em] ${stepDone ? "text-slate-900" : "text-slate-300"}`}
                      >
                        {step.label}
                      </span>
                    </div>
                  )
                })}
              </div>

              <div className="space-y-3 md:hidden">
                {filteredSteps.map((step, idx) => {
                  const stepDone = idx <= currentStatusIndex

                  const stepActive = idx === currentStatusIndex

                  return (
                    <div
                      key={step.label}
                      className={[
                        "flex items-center gap-3 rounded-xl border px-4 py-3 transition-all",
                        stepDone ? "border-slate-200 bg-white shadow-sm" : "border-slate-100 bg-slate-50/70",
                        stepActive ? `ring-2 ${step.ring}` : "",
                      ].join(" ")}
                    >
                      <div
                        className={[
                          "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg",
                          stepDone ? `${step.color} text-white` : "bg-white text-slate-300 ring-1 ring-slate-200",
                        ].join(" ")}
                      >
                        {stepDone && !stepActive ? <Check size={18} /> : <step.icon size={18} />}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-[10px] font-black uppercase tracking-[0.22em] ${stepDone ? "text-slate-900" : "text-slate-400"}`}
                        >
                          {t("publicMenu:tracking.step", { number: idx + 1 })}
                        </p>

                        <p className={`truncate text-sm font-bold ${stepDone ? "text-slate-900" : "text-slate-400"}`}>
                          {step.label}
                        </p>
                      </div>

                      {stepActive && (
                        <span className="h-2.5 w-2.5 rounded-full bg-orange-500 shadow-[0_0_0_6px_rgba(251,146,60,0.18)]" />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="grid gap-5 p-4 md:p-6 ">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-100 text-orange-600"
                  style={
                    isLegacyTheme
                      ? undefined
                      : { backgroundColor: publicMenuTheme.categoryBackgroundColor, color: publicMenuTheme.accentColor }
                  }
                >
                  <ShoppingBag size={18} />
                </div>

                <div>
                  <p
                    className="text-[10px] font-black uppercase tracking-[0.24em] text-orange-500"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                  >
                    {t("publicMenu:tracking.order_details")}
                  </p>

                  <h4
                    className="text-lg font-black text-slate-900"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                  >
                    {t("publicMenu:tracking.items_summary")}
                  </h4>
                </div>
              </div>

              <div className="space-y-4">
                {allItems.map((item: OrderDrink | OrderMeal, idx) => {
                  const isRodizioIncluded = item.chargeType === EOrderItemChargeType.RodizioIncluded

                  return (
                    <div
                      key={idx}
                      className={
                        isRodizioIncluded
                          ? "rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 shadow-sm"
                          : "group rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 p-4 shadow-sm transition hover:border-orange-200 hover:shadow-md"
                      }
                      style={
                        isLegacyTheme || isRodizioIncluded
                          ? undefined
                          : {
                              backgroundColor: publicMenuTheme.surfaceColor,
                              borderColor: publicMenuTheme.borderColor,
                              color: publicMenuTheme.textColor,
                            }
                      }
                    >
                      <div className="flex flex-col gap-4 sm:flex-row">
                        <img
                          src={item.getImage()}
                          className={
                            isRodizioIncluded
                              ? "h-14 w-14 rounded-lg object-cover shadow-sm sm:h-16 sm:w-16"
                              : "h-20 w-20 rounded-lg object-cover shadow-md sm:h-24 sm:w-24"
                          }
                        />

                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-1">
                              {!isRodizioIncluded && (
                                <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                  {t("publicMenu:tracking.item_number", { number: idx + 1 })}
                                </span>
                              )}

                              <h5
                                className="text-base font-black tracking-tight text-slate-900 sm:text-lg"
                                style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                              >
                                {item.getName()}
                              </h5>
                            </div>

                            {isRodizioIncluded ? (
                              <span className="inline-flex shrink-0 items-center rounded-lg border border-emerald-200 bg-emerald-100 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-emerald-700">
                                {t("rodizio:public.included")}
                              </span>
                            ) : (
                              <div
                                className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 text-left text-slate-800 shadow-sm sm:min-w-[140px] sm:text-right"
                                style={
                                  isLegacyTheme
                                    ? undefined
                                    : {
                                        backgroundColor: publicMenuTheme.surfaceColor,
                                        borderColor: publicMenuTheme.borderColor,
                                        borderWidth: publicMenuTheme.borderThickness,
                                        color: publicMenuTheme.textColor,
                                      }
                                }
                              >
                                <p
                                  className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500"
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                                >
                                  {t("publicMenu:tracking.item_total")}
                                </p>

                                <p
                                  className="mt-1 text-lg font-black text-orange-600"
                                  style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                                >
                                  R$ {item.valuePaid.toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>

                          {item.isReservation && (
                            <p className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700">
                              {t("publicMenu:tracking.reservation_summary", {
                                date: `${normalizeReservationDateForInput(item.reservationDate)}T00:00:00`.toReadableDate(
                                  "pt-BR",
                                  true,
                                ),
                                time: item.reservationTime,
                              })}
                            </p>
                          )}

                          {item.description && (
                            <p
                              className="rounded-xl border border-slate-100 bg-white px-4 py-3 text-sm font-medium italic text-slate-500"
                              style={
                                isLegacyTheme
                                  ? undefined
                                  : {
                                      backgroundColor: publicMenuTheme.surfaceColor,
                                      borderColor: publicMenuTheme.borderColor,
                                      color: publicMenuTheme.textColor,
                                    }
                              }
                            >
                              “{item.description}”
                            </p>
                          )}

                          {item.addtionals && item.addtionals.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {item.addtionals.map((add: any, aidx: number) => (
                                <div
                                  key={aidx}
                                  className="flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5"
                                  style={
                                    isLegacyTheme
                                      ? undefined
                                      : {
                                          backgroundColor: publicMenuTheme.categoryBackgroundColor,
                                          borderColor: publicMenuTheme.borderColor,
                                        }
                                  }
                                >
                                  <span
                                    className="text-[10px] font-black uppercase tracking-[0.15em] text-orange-700"
                                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                                  >
                                    + {add.addtionalName || t("publicMenu:tracking.additional")}
                                  </span>

                                  {!isRodizioIncluded && (
                                    <span className="text-[11px] font-bold text-orange-500">
                                      R$ {add.valueUnique.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {!isRodizioIncluded && (
                            <div className="grid gap-2 text-sm font-semibold text-slate-500 sm:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                                  {t("publicMenu:tracking.base")}
                                </span>

                                <span className="text-slate-700">R$ {item.valueBase.toFixed(2)}</span>
                              </div>

                              <div className="rounded-lg bg-orange-50 px-3 py-2 ring-1 ring-orange-100">
                                <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-orange-400">
                                  {t("publicMenu:tracking.additionals")}
                                </span>

                                <span className="text-orange-700">+ R$ {item.getValueAddtions().toFixed(2)}</span>
                              </div>

                              <div className="rounded-lg bg-emerald-50 px-3 py-2 ring-1 ring-emerald-100">
                                <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-emerald-400">
                                  {t("publicMenu:tracking.discount")}
                                </span>

                                <span className="text-emerald-700">- R$ {item.valueDiscount.toFixed(2)}</span>
                              </div>

                              <div className="rounded-lg bg-slate-100 px-3 py-2 text-gray-800 ring-1 ring-slate-300">
                                <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-gray/50">
                                  {t("publicMenu:tracking.subtotal")}
                                </span>
                                <span>R$ {item.getValueWithAddtions().toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="space-y-4">
              <div
                className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 text-slate-800 border-2 border-orange-200 shadow-sm md:p-6"
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor: publicMenuTheme.surfaceColor,
                        borderColor: publicMenuTheme.borderColor,
                        borderWidth: publicMenuTheme.borderThickness,
                        color: publicMenuTheme.textColor,
                      }
                }
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500"
                    style={
                      isLegacyTheme
                        ? undefined
                        : {
                            backgroundColor: publicMenuTheme.surfaceColor,
                            borderColor: publicMenuTheme.borderColor,
                            color: publicMenuTheme.accentColor,
                          }
                    }
                  >
                    <CreditCard size={18} />
                  </div>

                  <div>
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                    >
                      {t("publicMenu:tracking.financial_summary")}
                    </p>

                    <h4
                      className="text-base font-black"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                    >
                      {t("publicMenu:tracking.all_in_one_place")}
                    </h4>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <div
                    className="rounded-xl border-2 border-orange-200 bg-white p-4"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                    }
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                    >
                      {t("publicMenu:tracking.total_consumed")}
                    </p>

                    <p
                      className="mt-2 text-2xl font-black tracking-tight"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.cartTotalValueTextColor }}
                    >
                      R$ {order.orderPaymentEntity.totalFinal.toFixed(2)}
                    </p>
                  </div>

                  <div
                    className="rounded-xl border-2 border-orange-200 bg-white p-4"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                    }
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                    >
                      {t("publicMenu:tracking.last_update")}
                    </p>

                    <p
                      className="mt-2 text-sm font-bold text-slate-700"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                    >
                      {lastUpdateDate}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="overflow-hidden rounded-xl border-2 border-orange-200 bg-white shadow-sm"
                style={
                  isLegacyTheme
                    ? undefined
                    : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                }
              >
                <div
                  className="border-b border-slate-100 px-5 py-4"
                  style={isLegacyTheme ? undefined : { borderColor: publicMenuTheme.borderColor }}
                >
                  <p
                    className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.6 }}
                  >
                    {t("publicMenu:tracking.payment")}
                  </p>

                  <h4
                    className="mt-1 text-base font-black text-slate-900"
                    style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor }}
                  >
                    {t("publicMenu:tracking.payment_details")}
                  </h4>
                </div>
                <div className="p-2 sm:p-4">
                  <PaymentDetails order={order} />

                  {order.orderPaymentEntity.isOnlinePayment &&
                    renderProviderChargeReference(
                      order.orderPaymentEntity.externalReference,
                      t("publicMenu:tracking.external_reference"),
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div className="mx-auto max-w-7xl rounded-md pb-10">
        {activeOrders.length > 0 && (
          <div className="space-y-6 px-3 sm:px-4">
            <div
              className="overflow-hidden rounded-lg border-2 border-orange-200  px-4 py-5 shadow-sm sm:px-5"
              style={
                isLegacyTheme
                  ? undefined
                  : {
                      backgroundColor: publicMenuTheme.surfaceColor,
                      borderColor: publicMenuTheme.borderColor,
                      borderWidth: publicMenuTheme.borderThickness,
                    }
              }
            >
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-3">
                  <span
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-600"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: publicMenuTheme.categoryBackgroundColor, color: publicMenuTheme.accentColor }
                    }
                  >
                    {mode === EOrderPlaceType.Local ? <UtensilsCrossed size={14} /> : <Package size={14} />}
                    {t("publicMenu:tracking.realtime_command")}
                  </span>

                  <div>
                    <h2
                      className="text-2xl font-black uppercase tracking-tight sm:text-3xl"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.emptyStateTitleColor }}
                    >
                      {t("publicMenu:tracking.in_preparation")}
                    </h2>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[320px]">
                  <div
                    className="rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-slate-800 shadow-sm"
                    style={
                      isLegacyTheme
                        ? undefined
                        : {
                            backgroundColor: publicMenuTheme.surfaceColor,
                            borderColor: publicMenuTheme.borderColor,
                            color: publicMenuTheme.textColor,
                          }
                    }
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.7 }}
                    >
                      {t("publicMenu:tracking.active_commands")}
                    </p>

                    <p
                      className="mt-1 text-2xl font-black text-orange-600"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                    >
                      {activeOrders.length}
                    </p>
                  </div>

                  <div
                    className="rounded-xl border-2 border-orange-200 bg-white px-4 py-3 shadow-sm"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: publicMenuTheme.surfaceColor, borderColor: publicMenuTheme.borderColor }
                    }
                  >
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.textColor, opacity: 0.6 }}
                    >
                      {t("publicMenu:tracking.update")}
                    </p>

                    <div
                      className="mt-2 flex items-center gap-2 text-sm font-bold text-orange-600"
                      style={isLegacyTheme ? undefined : { color: publicMenuTheme.accentColor }}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-orange-500 animate-pulse" />
                      {lastUpdateDate}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {activeOrders.map(renderOrder)}
          </div>
        )}
      </div>
    </Animate>
  )
}

export default ComandaView
